import { createPortal } from "preact/compat";
import type { ComponentChildren } from "preact";
import { closeHint, coarsePointer } from "../press-word.ts";
import { useSheetLayer } from "../devsheet-hooks";
import { useScreenCue } from "./ScreenCue";
import { useExit } from "./useExit";

/**
 * The notice board's composers in the app's sheet (owner, 2026-10-05: "The location where the form to
 * enter stuff for the note page is confusing, should be a modal that opens over it"): the pinned-note
 * composer (src/components/PinnedNotes.tsx) and the weekly scroll's (src/components/WeeklyWriting.tsx)
 * open the same way, over the board, so nothing is inserted under it and the board never changes
 * height. It is the sheet What's new and the account sheet are: `.sheet-backdrop` with "Tap to close"
 * (closeHint) on its plaque above, `.sheet` on the wood in its frame, the same rise and stepped exit
 * (useExit), the same open and close cues, and useSheetLayer's Escape (and Android back), focus
 * trap, inert page and focus into its `data-autofocus` field. Inside, the composer as it was.
 *
 * Portalled to the body, as the photo check is, so the board's own transform (the fold's lag,
 * src/board-fold.ts) and a screen's slide can never hold it inside the column. With the keyboard up,
 * src/styles.css pins it to the visible part of the screen, so it stands on the keyboard.
 *
 * `children` gets the sheet's one close: `close()` is Cancel's (the backdrop and Escape too) and
 * `close(true)` a write's; `onClosed(done)` runs once the exit has played.
 */
export function ComposerSheet({ label, onClosed, children }: { label: string; onClosed: (done: boolean) => void; children: (close: (done?: boolean) => void) => ComponentChildren }) {
  const exit = useExit<[boolean?]>((done) => onClosed(!!done));
  useScreenCue(exit.closing);
  const close = (done = false) => exit.close(done);
  // Escape (and Android back) closes as Cancel; focus trapped, the app behind inert (src/focus-trap.ts).
  const sheetRef = useSheetLayer<HTMLDivElement>(!exit.closing, () => close());
  return createPortal(
    <div class={`sheet-backdrop composer-sheet-backdrop${exit.cls}`} ref={exit.ref} onClick={() => close()}>
      <span class="sheet-tap-close" aria-hidden="true">
        {closeHint()}
      </span>
      <div class="sheet composer-sheet" ref={sheetRef} role="dialog" aria-modal="true" tabIndex={-1} aria-label={label} onClick={(e) => e.stopPropagation()}>
        {children(close)}
      </div>
    </div>,
    document.body,
  );
}

/**
 * On a phone, Safari raises the keyboard only for a focus inside the tap itself, and the sheet's field
 * mounts a moment after it. So the tap that opens a composer focuses a stand-in field at once, which
 * brings the keyboard up; the sheet's field takes the focus from it when it mounts (the keyboard stays
 * up across the move) and the stand-in goes. Nothing on a desk, where there is no soft keyboard.
 */
export function holdKeyboard(): void {
  if (typeof document === "undefined" || !coarsePointer()) return;
  const stand = document.createElement("input");
  stand.type = "text";
  stand.tabIndex = -1;
  stand.setAttribute("aria-hidden", "true");
  // 16px, so iOS does not zoom to it; out of sight and out of the way of any tap.
  stand.style.cssText = "position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;border:0;padding:0;font-size:16px;pointer-events:none";
  document.body.appendChild(stand);
  stand.focus({ preventScroll: true });
  window.setTimeout(() => stand.remove(), 1000);
}
